import React from 'react';
import { useNavigate } from "react-router-dom";
import PageLevelChecks from './PageLevelChecks';
import useBatchUrlAnalysisProcessesPolling from '../process/hooks/useBatchUrlAnalysisProcessesPolling';
import { DPA_DASHBOARD_POLLING_TIME_MS } from '../settings';
import { useFetchOnPageWebsiteFailedChecksQuery } from './service';
import { groupClusterFailedChecks } from './utils';
import useHydrateFailedChecks from './hooks/useHydrateFailedChecks';
import { useFetchServiceableUrlsCountQuery } from '../serviceableUrls/service';

type Props = {
    websiteGuid: string;
};

export default function UrlFailedChecks({ websiteGuid }: Props) {
    const navigate = useNavigate();

    const { data: serviceableUrlsCount, isLoading: isLoadingUrlsCount } = useFetchServiceableUrlsCountQuery({ w_guid: websiteGuid });
    const { isRunningBatchAnalysis } = useBatchUrlAnalysisProcessesPolling(websiteGuid);
    const { data: clustersFailedChecks, isLoading: isLoadingFailedChecks } = useFetchOnPageWebsiteFailedChecksQuery(
        { w_guid: websiteGuid },
        {
            // The polling interval is in milliseconds!
            pollingInterval: isRunningBatchAnalysis ? DPA_DASHBOARD_POLLING_TIME_MS : 0,
            // The polling interval is reset if we call the hook again, refetchOnMount
            // Can take a number that will refetch if the cache value is older than the value
            // This is in seconds!
            refetchOnMountOrArgChange: isRunningBatchAnalysis ? DPA_DASHBOARD_POLLING_TIME_MS / 1000 : false,
        }
    );

    const urlFailedChecks = clustersFailedChecks ? groupClusterFailedChecks(clustersFailedChecks) : [];

    const { failedChecksWithDetails, isLoadingFailedChecksDetails } = useHydrateFailedChecks({
        partialFailedChecks: urlFailedChecks,
        totalUrlsCount: serviceableUrlsCount?.count,
    });

    const handleFailedCheckButtonClick = (seoTestKey: string) => {
        navigate(`/deep-analysis/failed-check/${seoTestKey}?w_guid=${websiteGuid}`);
    };

    const isLoading = isLoadingFailedChecks || isLoadingUrlsCount || isLoadingFailedChecksDetails;

    return (
        <PageLevelChecks
            title="Page-level SEO issues"
            type="url"
            failedChecks={failedChecksWithDetails}
            isLoading={isLoading}
            isRunningBatchAnalysis={isRunningBatchAnalysis}
            onFailedCheckButtonClicked={handleFailedCheckButtonClick}
            totalUrlsCount={serviceableUrlsCount?.count}
        />
    );
}
